import { useEffect } from "react";
import { LOGO_URL } from "./constants";

const DEFAULT_TITLE = "AI Shorthand — Universal Short-Command Prompt Dictionary™";
const DEFAULT_DESC = "The searchable AI Shorthand database with hundreds of instant short-command shortcuts for ChatGPT, Claude and Gemini. No prompt engineering needed.";

function setMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setJsonLd(data) {
  let el = document.getElementById("seo-jsonld");
  if (!data) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = "seo-jsonld";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

export const CANONICAL_ORIGIN = "https://web.aipromptshortcuts.app";

export function useSeo({ title, description, type = "website", jsonLd = null, noindex = false } = {}) {
  useEffect(() => {
    const t = title ? `${title} — AI Shorthand` : DEFAULT_TITLE;
    const d = description || DEFAULT_DESC;
    const url = CANONICAL_ORIGIN + window.location.pathname;
    document.title = t;
    setMeta("name", "description", d);
    setMeta("property", "og:title", t);
    setMeta("property", "og:description", d);
    setMeta("property", "og:type", type);
    setMeta("property", "og:url", url);
    setMeta("property", "og:image", `https://web.aipromptshortcuts.app${LOGO_URL}`);
    setMeta("property", "og:site_name", "Universal Short-Command Prompt Dictionary™");
    setMeta("name", "twitter:card", "summary");
    setMeta("name", "twitter:title", t);
    setMeta("name", "twitter:description", d);
    setMeta("name", "twitter:image", `https://web.aipromptshortcuts.app${LOGO_URL}`);
    setMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow");
    setCanonical(url);
    setJsonLd(jsonLd);
    return () => {
      document.title = DEFAULT_TITLE;
      setMeta("name", "description", DEFAULT_DESC);
      setMeta("property", "og:title", DEFAULT_TITLE);
      setMeta("property", "og:description", DEFAULT_DESC);
      setMeta("property", "og:type", "website");
      setMeta("name", "robots", "index, follow");
      setJsonLd(null);
    };
  }, [title, description, type, noindex]); // eslint-disable-line
}

export function orgWebsiteSchema() {
  const origin = CANONICAL_ORIGIN;
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${origin}/#organization`,
        name: "Universal Short-Command Prompt Dictionary™",
        alternateName: "AI Shorthand",
        legalName: "Voliciti Group Ventures Ltd",
        email: "info@virtualautochat.uk",
        url: origin,
        logo: LOGO_URL,
        sameAs: ["https://www.youtube.com/@AIPromptShortcutCommands", "https://www.linkedin.com/showcase/aipromptshortcuts", "https://www.instagram.com/aipromptshortcuts/", "https://www.facebook.com/aipromptshortcuts"],
        description: DEFAULT_DESC,
      },
      {
        "@type": "WebSite",
        "@id": `${origin}/#website`,
        name: "AI Shorthand",
        url: origin,
        publisher: { "@id": `${origin}/#organization` },
        potentialAction: {
          "@type": "SearchAction",
          target: { "@type": "EntryPoint", urlTemplate: `${origin}/categories?q={search_term_string}` },
          "query-input": "required name=search_term_string",
        },
      },
    ],
  };
}

export function faqSchema(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

export function pricingSchema() {
  const origin = CANONICAL_ORIGIN;
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: "AI Shorthand Membership — Universal Short-Command Prompt Dictionary™",
    description: "Searchable database of hundreds of short-command prompts for ChatGPT, Claude and Gemini.",
    image: LOGO_URL,
    brand: { "@type": "Brand", name: "AI Shorthand" },
    offers: [
      { "@type": "Offer", name: "Student Membership", price: "27.00", priceCurrency: "GBP", url: `${origin}/checkout/student_annual`, availability: "https://schema.org/InStock" },
      { "@type": "Offer", name: "Pro Membership", price: "47.00", priceCurrency: "GBP", url: `${origin}/checkout/pro_annual`, availability: "https://schema.org/InStock" },
      { "@type": "Offer", name: "Lifetime Membership", price: "99.00", priceCurrency: "GBP", url: `${origin}/checkout/lifetime`, availability: "https://schema.org/InStock" },
    ],
  };
}

export function blogPostSchema(post) {
  const origin = CANONICAL_ORIGIN;
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BlogPosting",
        headline: post.title,
        description: post.seo_description || post.excerpt,
        datePublished: post.published_at,
        dateModified: post.published_at,
        url: `${origin}/blog/${post.slug}`,
        image: LOGO_URL,
        author: { "@type": "Organization", name: "AI Shorthand" },
        publisher: { "@type": "Organization", name: "Universal Short-Command Prompt Dictionary™", logo: { "@type": "ImageObject", url: LOGO_URL } },
        mainEntityOfPage: `${origin}/blog/${post.slug}`,
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: origin },
          { "@type": "ListItem", position: 2, name: "Blog", item: `${origin}/blog` },
          { "@type": "ListItem", position: 3, name: post.title, item: `${origin}/blog/${post.slug}` },
        ],
      },
    ],
  };
}

export function breadcrumbSchema(items) {
  const origin = CANONICAL_ORIGIN;
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem", position: i + 1, name: it.name, item: origin + it.path,
    })),
  };
}
